import type { PendingValidationRound, WaiterTable } from './waiterApi';

/**
 * SC-976 design §2 D5 — le plafond d'une tablée : l'ancre plus 19
 * rattachées, jamais plus (6 jusqu'au 2026-09-14, 8 jusqu'au 2026-09-17).
 * Miroir de `MAX_TABLES_PER_SESSION` de sale_api. Un seul point de vérité —
 * `OpenTableModal` (ouverture multi-tables, cap des puces) et
 * `NotesListPage` (« Ajouter une table », message `TOO_MANY_TABLES`) en
 * dérivent tous deux au lieu d'écrire chacun leur propre chiffre (review
 * P2#5 : ils avaient dérivé, 5 et 6, sans lien entre eux).
 */
export const MAX_TABLES_PER_SESSION = 20;

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.2 D3 — four states a table
 * tile can show, derived purely from the `tables/` row (never from
 * `Table.status`, which sale_api only ever writes free↔occupied — spec §1).
 * A 5th signal, "à valider" (a QR round never reached the kitchen), is
 * orthogonal — it rides `hasPendingRound`, not this state, and can be true
 * alongside any of the four (see D3's badge).
 */
export type TableState = 'free' | 'seated' | 'ordered' | 'paid';

/** Canonical left-to-right order for the D2 legend-filter row. */
export const STATE_ORDER: TableState[] = ['free', 'seated', 'ordered', 'paid'];

// Spec §2.2 D3 predicate table, in the order it's written there: session
// null → free; round_count === 0 → seated; is_paid → paid; else ordered.
export function deriveTableState(table: WaiterTable): TableState {
  const session = table.session;
  if (session === null) return 'free';
  if (session.roundCount === 0) return 'seated';
  if (session.isPaid) return 'paid';
  return 'ordered';
}

/** D3 red badge — a guest QR round that never reached the kitchen. */
export function hasPendingRound(table: WaiterTable): boolean {
  return (table.session?.pendingValidation.length ?? 0) > 0;
}

/**
 * SC-789 — le NOMBRE écrit dans ce badge. Il comptait les commandes en attente
 * (`pendingValidation.length`), or les tournées successives d'une même table
 * fusionnent côté serveur dans UNE seule commande ancre (sale_api
 * `views_online.py`, branche `merge_order`) : la liste ne contient donc jamais
 * qu'une entrée, et une table où 7 plats attendaient d'être validés affichait
 * le même « 1 » qu'une table où un seul attendait. On compte les plats, comme
 * la feuille qui s'ouvre au clic (« n articles »).
 *
 * Repli sur le nombre de commandes si le décompte de lignes est nul : le badge
 * n'est affiché que lorsqu'il y a quelque chose à valider, il ne doit jamais
 * annoncer « 0 ».
 */
export function pendingItemsCount(rounds: PendingValidationRound[]): number {
  return rounds.reduce((total, round) => total + round.itemsCount, 0) || rounds.length;
}

/**
 * Spec 2026-08-28 paid-table-ordering-lock §5.2 — LA table dangereuse : elle a
 * deja paye, puis elle a rappele. `deriveTableState` la renvoie sur `'ordered'`
 * comme n'importe quelle table qui doit de l'argent, alors que celle-ci a un
 * convive qui se croit quitte et peut se lever. Signal orthogonal, comme
 * « a valider » : il peut coexister avec n'importe lequel des quatre etats.
 *
 * Se lit des montants, pas du verrou : le personnel a pu rouvrir la table (donc
 * plus de verrou) et c'est precisement la que la relance arrive.
 */
export function hasPaidRelance(table: WaiterTable): boolean {
  const paid = parseFloat(table.session?.paidTotal ?? '0');
  const due = parseFloat(table.session?.dueTotal ?? '0');
  return paid > 0 && due > 0;
}

/** Spec 2026-08-28 paid-table-ordering-lock — la table a ete soldee au QR et
 *  ne prend plus la commande du convive tant qu'un serveur ne la rouvre pas. */
export function isOrderingLocked(table: WaiterTable): boolean {
  return table.session?.orderingLocked === true;
}

/** spec 2026-08-28 §6 — un convive paie sa table depuis son telephone.
 *  Consultatif : ca n'empeche jamais d'encaisser au comptoir, ca previent. */
export function hasGuestPaymentPending(table: WaiterTable): boolean {
  return table.session?.guestPaymentPending === true;
}

/** D3 lock glyph 🔒 — the caisse has claimed (or is claiming) this table's
 *  bill. `failed` is deliberately excluded: the waiter can retry, it isn't
 *  locked. */
export function isLocked(table: WaiterTable): boolean {
  const status = table.session?.caisseHandoffStatus;
  return status === 'requested' || status === 'imported';
}

/** D1 salle bar selection — `'all'` shows everything, `'none'` isolates
 *  tables with no salle assigned (`area` null or absent), anything else is
 *  an `Area.uuid`. */
export type SalleKey = 'all' | 'none' | string;

export function filterTablesBySalle(tables: WaiterTable[], key: SalleKey): WaiterTable[] {
  if (key === 'all') return tables;
  if (key === 'none') return tables.filter((table) => !table.area);
  return tables.filter((table) => table.area?.uuid === key);
}

export interface TableStateCounts {
  free: number;
  seated: number;
  ordered: number;
  paid: number;
  pending: number;
}

/** D2 legend-filter counters — computed over whatever slice of `tables` the
 *  caller already narrowed to the selected salle. `pending` is orthogonal to
 *  the 4 states, so a table can be counted in both a state bucket and
 *  `pending`. */
export function tableStateCounts(tables: WaiterTable[]): TableStateCounts {
  const counts: TableStateCounts = { free: 0, seated: 0, ordered: 0, paid: 0, pending: 0 };
  for (const table of oneRowPerSession(tables)) {
    counts[deriveTableState(table)] += 1;
    if (hasPendingRound(table)) counts.pending += 1;
  }
  return counts;
}

/**
 * SC-976 — une tablée réunie compte pour UNE, quel que soit le nombre de
 * tables (demande utilisateur 2026-09-15 : « 3+4 » et « 6…11 » faisaient
 * 8 dans la pastille Sur place au lieu de 2). Les lignes membres portent la
 * MÊME session (même uuid) que leur ancre : on garde la première ligne de
 * chaque session. Dédupliquer par session plutôt que d'écarter
 * `role === 'member'` garde le compte juste quand le filtre de salle a retiré
 * l'ancre et que la membre y est seule (elle a alors sa propre tuile).
 * Les tables libres restent une par table.
 */
export function oneRowPerSession(tables: WaiterTable[]): WaiterTable[] {
  const seen = new Set<string>();
  return tables.filter((table) => {
    const sessionUuid = table.session?.uuid;
    if (!sessionUuid) return true;
    if (seen.has(sessionUuid)) return false;
    seen.add(sessionUuid);
    return true;
  });
}

/**
 * §2.7 tile correction — the tile shows `HH:MM · 1 h 20` once a table has
 * been open more than an hour; nothing extra below that. Returns `''` (not
 * rendered) under 60 minutes, else a French `H h MM` duration (zero-padded
 * minutes, e.g. `1 h 05`).
 */
export function elapsedLabel(openedAtIso: string, now: number | Date = Date.now()): string {
  const openedAt = Date.parse(openedAtIso);
  const nowMs = now instanceof Date ? now.getTime() : now;
  const minutes = Math.floor((nowMs - openedAt) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 60) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} h ${String(rest).padStart(2, '0')}`;
}

/**
 * DF-0383 — une table « à prendre » (ouverte, aucune tournée partie) dit depuis
 * combien de temps elle attend, dès l'ouverture. `elapsedLabel` se tait sous
 * une heure : chez Yuji, la S12 a affiché « 13:52 » pendant 12 min, la même
 * ligne qu'une table qui a commandé. Au-delà d'une heure l'unité reste écrite
 * (« 1 h 04 min ») : seul, « 1 h 04 » se lit comme une heure de la journée.
 * `''` seulement pour une date illisible.
 */
export function waitingLabel(openedAtIso: string, now: number | Date = Date.now()): string {
  const openedAt = Date.parse(openedAtIso);
  const nowMs = now instanceof Date ? now.getTime() : now;
  const minutes = Math.floor((nowMs - openedAt) / 60_000);
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  return `${elapsedLabel(openedAtIso, now)} min`;
}
